"use client";
import { Suspense, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Plus, Search, RefreshCw, ArrowRight, UserRound } from "lucide-react";
import { RequireRole } from "../console-shell";
import { Banner, Button, Desk, PageHeader, StatusTag } from ".";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { ReadState, useRead, type PageResult } from "./record-tools";
import { AccessNav, AccountReading } from "./access-reading";
import { AccessEditorForm } from "./access-editor";
import { type Account, type AccessEditor, type Gap } from "./access-model";
import { useSession } from "../../lib/session";
import { formatDate } from "../../lib/api";
export function AccountsWorkspace() {
  return (
    <RequireRole roleCode="ACCESS_ADMIN">
      <Suspense fallback={<Desk>正在读取账号…</Desk>}>
        <AccountDesk />
      </Suspense>
    </RequireRole>
  );
}
function AccountDesk() {
  const params = useSearchParams(),
    { whoami, refresh } = useSession();
  const [status, setStatus] = useState(""),
    [word, setWord] = useState(""),
    [q, setQ] = useState("");
  const [index, setIndex] = useState(0),
    [cursors, setCursors] = useState([""]),
    [selected, setSelected] = useState<string | null>(params.get("record")),
    [editor, setEditor] = useState<AccessEditor | null>(null),
    [message, setMessage] = useState("");
  const [savedGap, setSavedGap] = useState<{ gap: Gap; accountId: string } | null>(null);
  const focus = useRef<HTMLElement | null>(null),
    heading = useRef<HTMLHeadingElement>(null),
    opened = useRef("");
  const gapId = params.get("gap"),
    gaps = useRead<{ items: Gap[] }>(gapId ? "/api/v1/grants/appointment-gaps" : null);
  const list = useRead<PageResult<Account>>(
      `/api/v1/accounts?${new URLSearchParams({ limit: "20", ...(status ? { status } : {}), ...(q ? { q } : {}), ...(cursors[index] ? { cursor: cursors[index] } : {}) })}`
    ),
    detail = useRead<Account>(selected ? `/api/v1/accounts/${encodeURIComponent(selected)}` : null);
  useEffect(() => {
    const id = params.get("record");
    if (id) setSelected(id);
  }, [params]);
  useEffect(() => {
    const gap = gaps.data?.items.find((g) => g.appointmentId === gapId);
    if (gap && opened.current !== gap.appointmentId) {
      opened.current = gap.appointmentId;
      setEditor({ kind: "account", action: "create", gap });
    }
  }, [gaps.data, gapId]);
  const reset = () => {
    setCursors([""]);
    setIndex(0);
  };
  const open = (id: string) => {
    focus.current = document.activeElement as HTMLElement;
    setSelected(id);
  };
  if (editor)
    return (
      <Desk>
        <AccessEditorForm
          editor={editor}
          onCancel={() => setEditor(null)}
          onSaved={(result) => {
            const action = editor;
            setEditor(null);
            const id =
              (result as { accountId?: string }).accountId ??
              (action.kind === "account" ? action.record?.id : undefined);
            if (action.kind === "account" && action.action === "create") {
              setSavedGap(action.gap && id ? { gap: action.gap, accountId: id } : null);
              setMessage("账号已开通，请向本人交付登录信息，并继续核对业务授权。");
              reset();
            } else setMessage("账号办理已完成，正在读取最新记录。");
            if (id) setSelected(id);
            void list.reload();
            if (id === selected) void detail.reload();
            if (
              action.kind === "account" &&
              action.action === "unbind" &&
              action.record?.id === whoami?.accountId
            )
              void refresh();
          }}
        />
      </Desk>
    );
  return (
    <Desk wide>
      <PageHeader
        eyebrow="账号与授权 / 人员入口"
        title="账号管理"
        description="查找工作人员账号，核对登录方式与业务授权。"
        actions={
          <Button onClick={() => setEditor({ kind: "account", action: "create" })}>
            <Plus size={16} />
            开通账号
          </Button>
        }
      />
      <AccessNav current="accounts" />
      {message && <Banner tone="success">{message}</Banner>}
      {gapId && <ReadState {...gaps} retry={gaps.reload} />}
      <div className="access-filters" aria-label="账号状态筛选">
        {[
          ["", "全部"],
          ["ACTIVE", "在用"],
          ["DISABLED", "已停用"],
          ["NO_IDENTITY", "无登录方式"]
        ].map(([value, label]) => (
          <button
            key={value}
            aria-pressed={status === value}
            onClick={() => {
              setStatus(value);
              reset();
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="record-toolbar">
        <form
          className="record-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(word.trim());
            reset();
          }}
        >
          <Search size={17} />
          <input
            aria-label="搜索账号显示名或登录名"
            placeholder="搜索账号显示名或登录名"
            value={word}
            onChange={(e) => setWord(e.target.value)}
          />
          <Button type="submit" variant="outline">
            搜索
          </Button>
        </form>
        <Button variant="ghost" aria-label="刷新账号" onClick={list.reload}>
          <RefreshCw size={16} />
        </Button>
      </div>
      <ReadState {...list} retry={list.reload} />
      {list.data && (
        <>
          <div className="record-list-meta">
            <span>当前页 {list.data.items.length} 个账号</span>
            <span>账号状态与授权状态分别核对</span>
          </div>
          {list.data.items.length ? (
            <div className="record-table-scroll">
              <table className="record-table access-table">
                <caption className="sr-only">工作台账号</caption>
                <thead>
                  <tr>
                    <th>工作人员</th>
                    <th>登录方式</th>
                    <th>账号状态</th>
                    <th>生效授权</th>
                    <th>建立时间</th>
                    <th>
                      <span className="sr-only">详情</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <button className="access-record" onClick={() => open(a.id)}>
                          <UserRound size={18} />
                          <span>
                            <strong>{a.displayName}</strong>
                            {a.id === whoami?.accountId && <small>当前登录账号</small>}
                          </span>
                        </button>
                      </td>
                      <td>
                        {a.identities[0]?.usernameDisplay ??
                          (a.identities.length ? "已绑定登录方式" : "无可用登录方式")}
                        <small>{a.identities.length} 种</small>
                      </td>
                      <td>
                        <StatusTag tone={a.status === "ACTIVE" ? "success" : "neutral"}>
                          {a.status === "ACTIVE" ? "在用" : "已停用"}
                        </StatusTag>
                      </td>
                      <td>{a.activeGrantCount} 条</td>
                      <td>{formatDate(a.recordedAt)}</td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`查看${a.displayName}`}
                          onClick={() => open(a.id)}
                        >
                          <ArrowRight size={15} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="workspace-empty">
              <UserRound />
              <strong>没有匹配的账号</strong>
              <p>调整状态或搜索词后再试。</p>
            </div>
          )}
          <div className="record-pagination">
            <span>第 {index + 1} 页</span>
            <Button variant="outline" disabled={index === 0} onClick={() => setIndex(index - 1)}>
              上一页
            </Button>
            <Button
              variant="outline"
              disabled={!list.data.meta?.nextCursor}
              onClick={() => {
                setCursors((old) => [...old.slice(0, index + 1), list.data!.meta!.nextCursor!]);
                setIndex(index + 1);
              }}
            >
              下一页
            </Button>
          </div>
        </>
      )}
      <h2 ref={heading} tabIndex={-1} className="sr-only">
        账号工作区
      </h2>
      <Sheet
        open={!!selected}
        onOpenChange={(v) => {
          if (!v) setSelected(null);
        }}
      >
        <SheetContent
          className="record-sheet access-sheet workspace"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            (focus.current?.isConnected ? focus.current : heading.current)?.focus();
          }}
        >
          <SheetHeader>
            <p className="record-eyebrow">账号与授权</p>
            <SheetTitle>{detail.data?.displayName ?? "读取账号"}</SheetTitle>
            <SheetDescription>核对账号状态、登录方式和已有授权。</SheetDescription>
          </SheetHeader>
          <ReadState {...detail} retry={detail.reload} />
          {detail.data && (
            <AccountReading
              key={detail.data.id}
              account={detail.data}
              onAction={setEditor}
              afterCreate={
                savedGap?.accountId === detail.data.id && (
                  <Link
                    className="record-link"
                    href={`/grants?gap=${encodeURIComponent(savedGap.gap.appointmentId)}&account=${encodeURIComponent(detail.data.id)}`}
                  >
                    返回任职待授事项
                    <ArrowRight size={14} />
                  </Link>
                )
              }
            />
          )}
        </SheetContent>
      </Sheet>
    </Desk>
  );
}
